import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Yii } from "libraries/interfaces/Yii";
import { ICitizenShip, IHolder, IIdentityInfo, ISfpd } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { IDateFieldSpecs, IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { EIdentityInfoType, } from "libraries/component/identity-info-editor/identity-info-editor.interface";
import { Required } from "libraries/module/formbuilder/forms/utils/validators/required.validator";
import { IdentityInfoEditorWording } from "libraries/component/identity-info-editor/identity-info-editor-wording";
import { TravellerXGender } from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import { DateMomentPipe } from "libraries/date/dateMoment.pipe";
import { IdentityInfoEditorFormNaming } from "libraries/component/identity-info-editor/identity-info-editor-form-naming";
import { DateService } from "libraries/date/date.service";

interface IIdentityInfoFieldsSpecification {
    citizenshipFieldSpec: IFieldSpecs;
    numberFieldSpec: IFieldSpecs;
    countryFieldSpec: IFieldSpecs;
    validityEndFieldSpec: IDateFieldSpecs;
}

/**
 * Passport, NIC and SFPD editor component.
 */
@Component({
    selector: 'identity-info-editor',
    templateUrl: './identity-info-editor.component.html',
    styleUrls: ['./identity-info-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IdentityInfoEditorComponent implements OnChanges, OnInit, OnDestroy {
    /** Should we allow to select 'none' as document type. */
    @Input()
    public hasNoneOption: boolean = false;
    /** Should we show the 'birthDate' field. */
    @Input()
    public hasBirthDateField: boolean = true;
    @Input()
    public isTravellerReadonly: boolean = false;
    @Input()
    public travellerFormGroup: FormGroup;
    /** With live update. */
    @Input()
    public initialGender?: TravellerXGender = null;
    @Input()
    public initialCitizenship?: ICitizenShip = null;
    @Input()
    public initialBirthDate?: Date;
    @Input()
    public initialIdentityInfoType: EIdentityInfoType = EIdentityInfoType.passport;
    @Input()
    public initialPassport?: IIdentityInfo;
    @Input()
    public initialIdentityCard?: IIdentityInfo;
    @Input()
    public initialSfpd?: ISfpd;
    @Input()
    public apiServiceQualityMessage: string[];

    @Output()
    public readonly onBlur: EventEmitter<[FormGroup,IFieldSpecs]> = new EventEmitter();

    public readonly identityInfoTypes: typeof EIdentityInfoType = EIdentityInfoType;

    public readonly wording: IdentityInfoEditorWording = new IdentityInfoEditorWording();

    public readonly formNaming: IdentityInfoEditorFormNaming = new IdentityInfoEditorFormNaming();

    public readonly genderOptions: Array<{label: string; value: string }> = [
        { label: Yii.t('wui-shared-js', 'Homme'), value: 'male' },
        { label: Yii.t('wui-shared-js', 'Femme'), value: 'female' },
    ];

    public genderFieldSpec: IFieldSpecs;
    public birthDateFieldSpec: IDateFieldSpecs;

    public passportCitizenshipFieldSpec: IFieldSpecs;
    public passportNumberFieldSpec: IFieldSpecs;
    public passportCountryFieldSpec: IFieldSpecs;
    public passportValidityEndFieldSpec: IDateFieldSpecs;

    public identityCardCitizenshipFieldSpec: IFieldSpecs;
    public identityCardNumberFieldSpec: IFieldSpecs;
    public identityCardPlaceOfIssueFieldSpec: IFieldSpecs;
    public identityCardValidityEndFieldSpec: IDateFieldSpecs;

    protected withAsteriskIfRequired: boolean = true;

    constructor(protected readonly dateMomentPipe: DateMomentPipe,
        protected readonly dateService: DateService,
        protected readonly changeDetectorRef: ChangeDetectorRef) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.initialGender && changes.initialGender.currentValue
            && this.travellerFormGroup && this.travellerFormGroup.controls[this.formNaming.genderControlName]
            && (!this.travellerFormGroup.value[this.formNaming.genderControlName]
                || this.travellerFormGroup.value[this.formNaming.genderControlName] === changes.initialGender.previousValue)) {
            // we update gender if controlValue is null or equal to previousValue
            this.travellerFormGroup.controls[this.formNaming.genderControlName].patchValue(changes.initialGender.currentValue);
        }


    }

    /**
     * This form structure in inherited from IaQoBo.
     * IaQoBo interacts directly with the form, any modification of the form structure would require an update in IaQoBo.
     * @see traveller-group.ts
     */
    public ngOnInit(): void {
        if (!this.travellerFormGroup.contains(this.formNaming.identityTypeControlName)) {
            this.travellerFormGroup.setControl(
                this.formNaming.identityTypeControlName,
                new FormControl(this.initialIdentityInfoType, Validators.required));
        }

        // COMMON
        if (!this.travellerFormGroup.contains(this.formNaming.genderControlName)) {
            this.travellerFormGroup.setControl(this.formNaming.genderControlName, new FormControl(this.initialGender));
        }
        this.genderFieldSpec = {
            formGroup: this.travellerFormGroup,
            formControlName: this.formNaming.genderControlName,
            label: this.wording.gender,
            validators: [new Required()],
        };

        if (this.hasBirthDateField) {
            if (!this.travellerFormGroup.contains(this.formNaming.birthDateControlName)) {
                this.travellerFormGroup.setControl(this.formNaming.birthDateControlName, new FormControl(this.initialBirthDate));
            }
            this.birthDateFieldSpec = {
                formGroup: this.travellerFormGroup,
                formControlName: this.formNaming.birthDateControlName,
                label: this.wording.birthDate,
                icon: 'fa fa-calendar',
                validators: [new Required()],
            };
        }

        // PASSPORT
        const passportFieldsSpecification: IIdentityInfoFieldsSpecification = this.buildIdentityInfoFormAndFields(
            this.initialPassport,
            this.formNaming.passportCitizenshipControlName,
            this.formNaming.passportGroupName,
            this.formNaming.passNumberControlName,
            this.formNaming.passCountryControlName,
            this.formNaming.passEndControlName,
            this.wording.passportNumber);

        this.passportCitizenshipFieldSpec = passportFieldsSpecification.citizenshipFieldSpec;
        this.passportNumberFieldSpec = passportFieldsSpecification.numberFieldSpec;
        this.passportCountryFieldSpec = passportFieldsSpecification.countryFieldSpec;
        this.passportValidityEndFieldSpec = passportFieldsSpecification.validityEndFieldSpec;

        // IDENTITY CARD
        const identityCardFieldsSpecification: IIdentityInfoFieldsSpecification = this.buildIdentityInfoFormAndFields(
            this.initialIdentityCard,
            this.formNaming.identityCardCitizenshipControlName,
            this.formNaming.identityCardGroupName,
            this.formNaming.identityCardNumberControlName,
            this.formNaming.cardCountryControlName,
            this.formNaming.cardEndControlName,
            this.wording.identityCardNumber);
        this.identityCardCitizenshipFieldSpec = identityCardFieldsSpecification.citizenshipFieldSpec;
        this.identityCardNumberFieldSpec = identityCardFieldsSpecification.numberFieldSpec;
        this.identityCardPlaceOfIssueFieldSpec = identityCardFieldsSpecification.countryFieldSpec;
        this.identityCardValidityEndFieldSpec = identityCardFieldsSpecification.validityEndFieldSpec;

        this.applyIdentityTypeOnFormGroup();

        if (this.isTravellerReadonly) {
            this.passportCitizenshipFieldSpec.disabled = true;
            this.passportNumberFieldSpec.disabled = true;
            this.passportCountryFieldSpec.disabled = true;
            this.passportValidityEndFieldSpec.disabled = true;

            this.identityCardCitizenshipFieldSpec.disabled = true;
            this.identityCardNumberFieldSpec.disabled = true;
            this.identityCardPlaceOfIssueFieldSpec.disabled = true;
            this.identityCardValidityEndFieldSpec.disabled = true;

            this.genderFieldSpec.disabled = true;
        }

        if(this.apiServiceQualityMessage){
            if(this.apiServiceQualityMessage[0] === "APIS"){
                this.travellerFormGroup.get(this.formNaming.identityTypeControlName).patchValue(EIdentityInfoType.passport);
            }
            if(this.apiServiceQualityMessage[0] === "SFPD"){
                this.travellerFormGroup.get(this.formNaming.identityTypeControlName).patchValue(EIdentityInfoType.secureFlightPassengerData);
            }
            this.applyIdentityTypeOnFormGroup();
        }
    }

    public ngOnDestroy(): void {
        this.disableCommonFormControls();
        this.disablePassportFormControls();
        this.disableIdentityCardFormControls();
    }

    public applyIdentityTypeOnFormGroup(): void {
        const identityTypeValue: EIdentityInfoType|null = this.travellerFormGroup.value[this.formNaming.identityTypeControlName];
        if (identityTypeValue) {
            this.travellerFormGroup.controls[this.formNaming.birthDateControlName].enable();
            this.travellerFormGroup.controls[this.formNaming.genderControlName].enable();
        } else {
            this.disableCommonFormControls();
        }
        if (identityTypeValue ===  EIdentityInfoType.passport) {
            this.travellerFormGroup.controls[this.formNaming.passportCitizenshipControlName].enable();
            this.travellerFormGroup.controls[this.formNaming.passportGroupName].enable();
        } else {
            this.disablePassportFormControls();
        }
        if (identityTypeValue === EIdentityInfoType.identityCard) {
            this.travellerFormGroup.controls[this.formNaming.identityCardCitizenshipControlName].enable();
            this.travellerFormGroup.controls[this.formNaming.identityCardGroupName].enable();
        } else {
            this.disableIdentityCardFormControls();
        }
        this.changeDetectorRef.markForCheck();
    }

    public onParentFormCheck(): void {
        this.changeDetectorRef.markForCheck();
    }

    public getXGender(): TravellerXGender {
        return this.travellerFormGroup.value[this.formNaming.genderControlName];
    }

    public getBirthDateAsString(): string {
        const myBirthDate: Date = this.travellerFormGroup.value[this.formNaming.birthDateControlName];
        return myBirthDate ? this.dateMomentPipe.transform(myBirthDate, DateMomentPipe.ISO_DATE_FORMAT) : null;
    }

    public getPassportInArray(firstName: string, lastName: string): IIdentityInfo[] {
        if (this.travellerFormGroup.value[this.formNaming.identityTypeControlName] !== EIdentityInfoType.passport) {
            return [];
        }
        return [(this.buildIdentityInfo(
            this.travellerFormGroup.value[this.formNaming.passportGroupName][this.formNaming.passNumberControlName],
            this.travellerFormGroup.value[this.formNaming.passportGroupName][this.formNaming.issuanceGroupName][this.formNaming.passCountryControlName],
            this.travellerFormGroup.value[this.formNaming.passportGroupName][this.formNaming.validityGroupName][this.formNaming.passEndControlName],
            this.travellerFormGroup.value[this.formNaming.passportCitizenshipControlName],
            firstName,
            lastName))];
    }

    public getIdentityCardInArray(firstName: string, lastName: string): IIdentityInfo[] {
        if (this.travellerFormGroup.value[this.formNaming.identityTypeControlName] !== EIdentityInfoType.identityCard) {
            return [];
        }
        return [(this.buildIdentityInfo(
            this.travellerFormGroup.value[this.formNaming.identityCardGroupName][this.formNaming.identityCardNumberControlName],
            this.travellerFormGroup.value[this.formNaming.identityCardGroupName][this.formNaming.issuanceGroupName][this.formNaming.cardCountryControlName],
            this.travellerFormGroup.value[this.formNaming.identityCardGroupName][this.formNaming.validityGroupName][this.formNaming.cardEndControlName],
            this.travellerFormGroup.value[this.formNaming.identityCardCitizenshipControlName],
            firstName,
            lastName))];
    }

    public getSecureFlightPassengerData(firstName: string, lastName: string): ISfpd|null {
        if (this.travellerFormGroup.value[this.formNaming.identityTypeControlName] !== EIdentityInfoType.secureFlightPassengerData) {
            return null;
        }
        return {
            holder: this.buildHolder(null, firstName, lastName)
        };
    }

    public getCitizenShip(): ICitizenShip|null {
        const passCitizenship: string = this.travellerFormGroup.value[this.formNaming.passportCitizenshipControlName];
        if (passCitizenship) {
            return buildCitizenShip(passCitizenship);
        }
        const cardCitizenship: string = this.travellerFormGroup.value[this.formNaming.identityCardCitizenshipControlName];
        if (cardCitizenship) {
            return buildCitizenShip(cardCitizenship);
        }
        return null;
    }

    private buildIdentityInfoFormAndFields(
        initialIdentityInfoArray: IIdentityInfo,
        citizenshipControlName: string,
        identityInfoGroupName: string,
        numberControlName: string,
        countryControlName: string,
        validityEndControlName: string,
        numberWording: string
    ): IIdentityInfoFieldsSpecification {

        const withAsteriskIfRequired: boolean = this.withAsteriskIfRequired;

        const initialIdentityInfo: IIdentityInfo = initialIdentityInfoArray;
        // citizenship
        if (!this.travellerFormGroup.contains(citizenshipControlName)) {
            this.travellerFormGroup.setControl(
                citizenshipControlName,
                new FormControl(this.initialCitizenship ? this.initialCitizenship.country.code : null));
        }
        const citizenshipFieldSpec: IFieldSpecs = {
            withAsteriskIfRequired,
            formGroup: this.travellerFormGroup,
            formControlName: citizenshipControlName,
            label: this.wording.nationality,
            validators: [new Required()],
        };

        // other fields
        if (!this.travellerFormGroup.contains(identityInfoGroupName)) {
            this.travellerFormGroup.setControl(identityInfoGroupName,
                new FormGroup({
                    [numberControlName]: new FormControl(initialIdentityInfo ? initialIdentityInfo.number : null),
                    [this.formNaming.issuanceGroupName]: new FormGroup({
                        [countryControlName]: new FormControl(initialIdentityInfo ? initialIdentityInfo.holder.citizenShip.country.code : null)
                    }),
                    [this.formNaming.validityGroupName]: new FormGroup({
                        [validityEndControlName]: new FormControl(
                            initialIdentityInfo && initialIdentityInfo.validity && initialIdentityInfo.validity.end
                                ? this.dateService.getStr2JsDate(initialIdentityInfo.validity.end)
                                : null
                        )
                    }),
            }));
        }

        const numberFieldSpec: IFieldSpecs = {
            withAsteriskIfRequired,
            formGroup: this.travellerFormGroup.controls[identityInfoGroupName] as FormGroup,
            formControlName: numberControlName,
            label: numberWording,
            validators: [new Required()],
        };

        const countryFieldSpec: IFieldSpecs = {
            withAsteriskIfRequired,
            formGroup: this.travellerFormGroup.get([identityInfoGroupName, this.formNaming.issuanceGroupName]) as FormGroup,
            formControlName: countryControlName,
            label: this.wording.identityPlaceOfIssue,
            validators: [new Required()],
        };

        const validityEndFieldSpec: IDateFieldSpecs = {
            withAsteriskIfRequired,
            formGroup: this.travellerFormGroup.get([identityInfoGroupName, this.formNaming.validityGroupName]) as FormGroup,
            formControlName: validityEndControlName,
            label: this.wording.validityEnd,
            icon: 'fa fa-calendar',
            validators: [new Required()],
        };

        if (initialIdentityInfo) {
            if (initialIdentityInfo.holder &&
                initialIdentityInfo.holder.citizenShip) {
                const countryCitizenShipStr: string = typeof initialIdentityInfo.holder.citizenShip.country === 'string'
                    ? initialIdentityInfo.holder.citizenShip.country
                    : initialIdentityInfo.holder.citizenShip.country.code;

                citizenshipFieldSpec.formGroup.get(citizenshipControlName).setValue(countryCitizenShipStr);
            }

            numberFieldSpec.formGroup.get(numberControlName).setValue(initialIdentityInfo.number);

            const countryPassIsussanceStr: string = typeof initialIdentityInfo.placeOfIssue.country === 'string'
                ? initialIdentityInfo.placeOfIssue.country
                : initialIdentityInfo.placeOfIssue.country.code;

            countryFieldSpec.formGroup.get(countryControlName).setValue(countryPassIsussanceStr);

            initialIdentityInfo.validity && initialIdentityInfo.validity.end &&
            validityEndFieldSpec.formGroup.get(validityEndControlName).setValue(initialIdentityInfo.validity.end);
        }

        // workaround for IaQoBo where the validityEnd value is converted to a string... we convert it back to a Date.
        const validityEndControl: FormControl =
            this.travellerFormGroup.get([identityInfoGroupName, this.formNaming.validityGroupName, validityEndControlName]) as FormControl;
        if (validityEndControl.value && !(validityEndControl.value instanceof Date)) {
            validityEndControl.patchValue(this.dateService.getStr2JsDate(validityEndControl.value));
        }

        return {citizenshipFieldSpec, numberFieldSpec, countryFieldSpec, validityEndFieldSpec};
    }

    private buildIdentityInfo(
        number: string,
        placeOfIssueCode: string,
        validityEnd: string,
        citizenShipCode: string,
        firstName: string,
        lastName: string
    ): IIdentityInfo {
        return {
            number,
            placeOfIssue: {
                country: {
                    code: placeOfIssueCode
                }
            },
            validity: {
                end: this.dateMomentPipe.transform(validityEnd, DateMomentPipe.ISO_DATE_FORMAT)
            },
            holder: this.buildHolder(citizenShipCode, firstName, lastName)
        };
    }

    private buildHolder(citizenShipCode: string, firstName: string, lastName: string): IHolder {
        return {
            xGender: this.getXGender(),
            birthDate: this.getBirthDateAsString(),
            firstName,
            lastName,
            citizenShip: buildCitizenShip(citizenShipCode)
        };
    }

    private disableCommonFormControls(): void {
        this.travellerFormGroup.controls[this.formNaming.genderControlName].disable();
        if (this.hasBirthDateField) {
            this.travellerFormGroup.controls[this.formNaming.birthDateControlName].disable();
        }
    }

    private disablePassportFormControls(): void {
        this.travellerFormGroup.controls[this.formNaming.passportCitizenshipControlName].disable();
        this.travellerFormGroup.controls[this.formNaming.passportGroupName].disable();
    }

    private disableIdentityCardFormControls(): void {
        this.travellerFormGroup.controls[this.formNaming.identityCardCitizenshipControlName].disable();
        this.travellerFormGroup.controls[this.formNaming.identityCardGroupName].disable();
    }
}

function buildCitizenShip(citizenShipCode: string): ICitizenShip {
    return citizenShipCode ? {country: {code: citizenShipCode}} : null;
}

